import type { MechanicDefinition } from "./types";

export const fanFiction: MechanicDefinition = {
  id: "fan_fiction",
  name: "Online Research",
  description: "Import reference material from the web. Built-in Google Search, Wikipedia, and Fandom wiki let the AI absorb lore, facts, and world rules to enrich your story. Also enables dramatic narrative tropes like plot twists, crossovers, and relationship dynamics.",
  icon: "Heart",
  category: "narrative",
  aiHints: {
    purpose: "Enables web research for lore import plus dramatic tropes like shipping, plot twists, betrayals, and crossovers.",
    whenToUse: "Worlds that draw from existing lore or reference material. Also great for dramatic, romance-heavy, or high-stakes narratives.",
    synergies: ["narrative_tone", "reactive_world", "dialogue_system"],
    conflicts: [],
    prerequisites: [],
    tags: ["research", "lore-import", "drama", "romance", "shipping", "twists", "tropes"],
    complexity: 1,
    exampleConfig: { drama_level: "high", shipping: true, plot_twists: true },
    stateVariables: [],
  },
  configSchema: [
    {
      key: "intensity",
      label: "Drama Intensity",
      type: "select",
      options: [
        { value: "subtle", label: "Subtle (gentle hints)" },
        { value: "moderate", label: "Moderate (clear drama)" },
        { value: "maximum", label: "Maximum Drama (soap opera)" },
      ],
      default: "moderate",
    },
    {
      key: "shipping",
      label: "Enable Shipping",
      type: "boolean",
      default: true,
      description: "Allow romantic subplots and relationship dynamics between NPCs and player",
    },
    {
      key: "crossovers",
      label: "Allow Crossovers",
      type: "boolean",
      default: false,
      description: "Characters from other worlds/franchises can appear",
    },
    {
      key: "plot_twists",
      label: "Plot Twist Frequency",
      type: "select",
      options: [
        { value: "rare", label: "Rare (every 10+ turns)" },
        { value: "regular", label: "Regular (every 5-8 turns)" },
        { value: "frequent", label: "Frequent (every 2-3 turns)" },
      ],
      default: "regular",
    },
  ],
  systemPromptSection: (config) => {
    const intensity = config.intensity || "moderate";
    const shipping = config.shipping !== false;
    const crossovers = config.crossovers === true;
    const twists = config.plot_twists || "regular";
    const intensityDesc: Record<string, string> = {
      subtle: "Hints at deeper emotions, unspoken tensions, meaningful glances. Drama simmers beneath the surface.",
      moderate: "Clear emotional arcs, dramatic confrontations, relationship developments. Characters express feelings openly.",
      maximum: "Full soap opera: betrayals, secret identities, dramatic monologues, love triangles, shocking revelations. Every scene has emotional stakes.",
    };
    const twistFreq: Record<string, string> = {
      rare: "Major plot twists are rare — build toward them carefully over many turns.",
      regular: "Introduce a surprising twist or revelation every 5-8 turns.",
      frequent: "Something unexpected happens almost every other scene. Keep players on their toes.",
    };
    return `## FAN FICTION MODE (Flow Mechanic — Director + Narrator + Character Agents)
- Drama intensity: ${intensity}
- ${intensityDesc[intensity]}
${shipping ? `- SHIPPING ENABLED: NPCs can develop romantic feelings for the player or each other. Track relationship_tension in entity properties. Build chemistry through dialogue and shared experiences. Character agents should amplify romantic tension or rivalry when invoked.` : "- Shipping disabled."}
${crossovers ? `- CROSSOVERS ENABLED: Characters from other worlds, stories, or genres may appear. They bring their own lore but adapt to the current world's rules. Make crossovers feel earned and surprising.` : ""}
- PLOT TWISTS: ${twistFreq[twists]}
  - The Director seeds drama triggers and twist setups at the configured frequency.
  - Types of twists: secret identities, betrayals, hidden allegiances, prophecy fulfillment, lost memories, family connections, villain redemption, hero corruption.
  - Foreshadow twists subtly before revealing them. Use NPC entity_memories to plant seeds.
- DRAMATIC REVEALS: When a major secret is uncovered, pause the narrative. Describe the character's reaction. Use generate_character_memories to capture how NPCs process the revelation.
- TROPES TO EMBRACE: found family, rivals-to-friends, redemption arcs, power of friendship, dramatic sacrifices, "I knew all along", cliffhangers before rest scenes.
- AVOID: character assassination, forced romance, deus ex machina without foreshadowing.
- CANON GROUNDING (HARD RULE): NEVER invent canonical counts, named characters, or established identities from the source material. If the player asks "how many X", "who is on the bus", "name the survivors", "identify the people in the room", or any similar inspection/identification question, you MUST call \`search_lore_online\` (with the \`franchise\` parameter) BEFORE answering. If the canonical answer is unknown or the tool returns nothing, narrate in-world uncertainty instead of fabricating numbers or names. Fabricating canon is a hard failure.`;
  },
  tools: [],
};
